import React, { useContext, useEffect } from 'react';
import { Typography, Button, Card, CardContent, Box } from '@mui/material';
import { useDispatch } from 'react-redux';
import { Capacitor } from '@capacitor/core';
import Grid from '@mui/material/Grid';
import { useHistory } from 'react-router-dom';
import * as uiPersistedActions from '../actions/uiPersisted';
import routes from '../routes';
import * as uiActions from '../actions/ui';
import { nativeRescuerWelcomeTour, siteDirectorTour } from '../helpers/tours';
import * as authService from '../services/auth';
import { Roles } from '../models/roles';
import welcomeInfographic from '../assets/images/Infographic.png';
import welcomeInfographicMobile from '../assets/images/infographic-mobile.png';
import useActiveUser from '../hooks/useActiveUser';
import { hasRoleInCurrentlySelectedSite, shouldHideMainDrawer } from '../services/auth';
import { setFABActions } from '../helpers/fab';
import FABContext from '../context/FABContext/FABContext';
import { getVerificationStatus } from '../api/verification';
import { setUIDisabled } from '../actions/ui';
import useActiveSite from '../hooks/useActiveSite';

const WelcomePageView = ({ isMobileView }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const activeUser = useActiveUser();
  const activeSite = useActiveSite();
  const fabContext = useContext(FABContext);

  const setTourStepper = (enabled, payload) => dispatch(uiActions.setTourStepper(enabled, payload));

  const isRescuer = hasRoleInCurrentlySelectedSite(activeUser, Roles.Rescuer);

  const onGetStarted = () => {
    // Show welcome tour on native for user with rescuer role
    if (Capacitor.isNativePlatform() && isRescuer) {
      history.push(routes.index);
      setTimeout(() => {
        setTourStepper(
          true,
          nativeRescuerWelcomeTour({
            firstname: activeSite.primary_contact_firstname,
            lastname: activeSite.primary_contact_lastname,
            email: activeSite.primary_contact_email,
          })
        );
      }, 700);
      return;
    }

    history.push(routes.index);
  };

  useEffect(() => {
    // reset default dashboard just in case if user re-login to different account
    dispatch(uiPersistedActions.resetDefaultDashboard());
    if (!shouldHideMainDrawer(activeUser, isMobileView)) {
      dispatch(uiPersistedActions.openDrawer());
    }

    const isFoodDonorAdmin = hasRoleInCurrentlySelectedSite(activeUser, Roles.DonorLocationAdmin);
    if (isFoodDonorAdmin) {
      const donorLocation = activeUser.role_assignments.find(role => role.role_name === Roles.DonorLocationAdmin);

      (async () => {
        const verificationStatusResponse = await getVerificationStatus({
          entityId: donorLocation.donor_location_id,
          entityType: 'food_donor',
        });

        const verificationStatusResponseData = await verificationStatusResponse.json();
        dispatch(
          setUIDisabled({
            status:
              verificationStatusResponseData.data.length >= 1 &&
              verificationStatusResponseData.data[0].status !== 'approved',
          })
        );
      })();
    }

    return () => {
      // trigger set floating action button actions on component will unmount
      // that way we can cover all of the possible cases from exiting / skipping the "Welcome Page"
      return setFABActions(activeUser, history, fabContext);
    };
  }, [dispatch]);

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography color="textSecondary" gutterBottom data-introid="welcome-message">
          Welcome to Food Rescue US
        </Typography>

        <Box
          mt={1}
          mb={2}
          sx={{
            '& button:not(:first-child)': {
              marginLeft: 1,
              marginRight: 1,
            },
          }}
        >
          <Button
            data-introid="continue-button"
            variant="contained"
            color="primary"
            onClick={onGetStarted}
          >
            Get started
          </Button>

          {authService.currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite([
            Roles.SiteDirector,
            Roles.SiteCoordinator,
          ]) && (
            <Button variant="outlined" onClick={() => setTourStepper(true, siteDirectorTour)}>
              Take a Tour
            </Button>
          )}
        </Box>

        <Grid container direction="row" justifyContent="center" alignItems="center">
          <Grid item xs={12} lg={10} xl={8} sx={{ textAlign: 'center' }}>
            <img
              width="100%"
              alt="welcome graphic"
              src={isMobileView ? welcomeInfographicMobile : welcomeInfographic}
              style={{
                height: isMobileView ? '100%' : '75vh',
                width: isMobileView ? '100%' : 'auto',
              }}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default WelcomePageView;
